import { useEffect, useState } from "react";
import { Building2, Loader2, AlertCircle, Tablet, Users as UsersIcon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ApiError, devices as devicesApi, orgs as orgsApi, users as usersApi } from "@/lib/api";

interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  enabled_apps: string[];
  user_count: number;
  device_count: number;
  active_device_count: number;
}

export default function AdminOrganisation() {
  const [data, setData] = useState<OrgSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [org, users, devs] = await Promise.all([
          orgsApi.me(),
          usersApi.list(),
          devicesApi.list(),
        ]);
        setData({
          id: org.id,
          name: org.name,
          slug: org.slug,
          enabled_apps: org.enabled_apps,
          user_count: users.length,
          device_count: devs.length,
          active_device_count: devs.filter((d) => !d.revoked).length,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Failed to load organisation");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Building2 className="h-6 w-6" /> Organisation
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tenant settings + at-a-glance counts.
        </p>
      </div>

      {error && (
        <Card className="mb-4 border-destructive bg-destructive/10">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </CardContent>
        </Card>
      )}

      {!loading && data && (
        <div className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Field label="Name" value={data.name} />
              <Field label="Slug" value={data.slug} mono />
              <Field label="Tenant ID" value={data.id} mono />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Enabled apps</p>
                <div className="flex flex-wrap gap-1">
                  {data.enabled_apps.map((a) => (
                    <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <UsersIcon className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{data.user_count}</p>
                  <p className="text-sm text-muted-foreground">Users</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <Tablet className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {data.active_device_count}
                    {data.device_count !== data.active_device_count && (
                      <span className="text-sm font-normal text-muted-foreground"> / {data.device_count}</span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {data.active_device_count === data.device_count ? "Active tablets" : "Active / total tablets"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-sm text-foreground ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}
